import React, { createContext, useContext, useReducer, useEffect } from "react";
import type {
  GpsPosition,
  RunData,
  GateLine,
  RunResult,
} from "@/lib/gps/types";
import type { ConeCandidate } from "@/lib/gps/cone-detector";

const HISTORY_KEY = "run_history";
const MAX_HISTORY = 20;

interface RunState {
  run: RunData;
  history: RunResult[];
  detectedCones: ConeCandidate[];
}

type RunAction =
  | { type: "ADD_POSITION"; position: GpsPosition }
  | { type: "START_RUN"; timestamp: number }
  | { type: "END_RUN"; timestamp: number }
  | { type: "ARM_RUN" }
  | { type: "DISARM_RUN" }
  | { type: "CROSS_START"; timestamp: number }
  | { type: "CROSS_FINISH"; timestamp: number }
  | { type: "RESET_RUN" }
  | { type: "SET_START_GATE"; gate: GateLine }
  | { type: "SET_END_GATE"; gate: GateLine }
  | { type: "IMPORT_HISTORY"; results: RunResult[] }
  | { type: "SET_DETECTED_CONES"; cones: ConeCandidate[] };

const initialRun: RunData = {
  id: crypto.randomUUID?.() || Date.now().toString(),
  positions: [],
  startTime: null,
  endTime: null,
  startGate: null,
  endGate: null,
  status: "idle",
  totalDistance: 0,
};

function loadHistory(): RunResult[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as RunResult[]) : [];
  } catch {
    return [];
  }
}

function saveHistory(history: RunResult[]) {
  try {
    localStorage.setItem(
      HISTORY_KEY,
      JSON.stringify(history.slice(-MAX_HISTORY)),
    );
  } catch {}
}

function runReducer(state: RunState, action: RunAction): RunState {
  switch (action.type) {
    case "ADD_POSITION":
      return {
        ...state,
        run: {
          ...state.run,
          positions: [...state.run.positions, action.position],
        },
      };
    case "START_RUN":
      return {
        ...state,
        run: {
          ...state.run,
          status: "running",
          startTime: action.timestamp,
          positions: [],
          id: crypto.randomUUID?.() || Date.now().toString(),
        },
      };
    case "END_RUN":
      return {
        ...state,
        run: {
          ...state.run,
          status: "finished",
          endTime: action.timestamp,
        },
      };
    case "ARM_RUN":
      return {
        ...state,
        run: { ...state.run, status: "armed" },
      };
    case "DISARM_RUN":
      return {
        ...state,
        run: { ...state.run, status: "idle" },
      };
    case "CROSS_START":
      return {
        ...state,
        run: {
          ...state.run,
          status: "running",
          startTime: action.timestamp,
          positions: [],
          id: crypto.randomUUID?.() || Date.now().toString(),
        },
      };
    case "CROSS_FINISH":
      return {
        ...state,
        run: {
          ...state.run,
          status: "finished",
          endTime: action.timestamp,
        },
      };
    case "RESET_RUN":
      return {
        ...state,
        run: {
          ...initialRun,
          id: crypto.randomUUID?.() || Date.now().toString(),
        },
      };
    case "SET_START_GATE":
      return {
        ...state,
        run: { ...state.run, startGate: action.gate },
      };
    case "SET_END_GATE":
      return {
        ...state,
        run: { ...state.run, endGate: action.gate },
      };
    case "IMPORT_HISTORY": {
      const merged = [...state.history, ...action.results];
      const deduped = merged.filter(
        (r, i) => merged.findIndex((m) => m.id === r.id) === i,
      );
      saveHistory(deduped);
      return { ...state, history: deduped };
    }
    case "SET_DETECTED_CONES":
      return { ...state, detectedCones: action.cones };
    default:
      return state;
  }
}

interface RunContextValue extends RunState {
  dispatch: React.Dispatch<RunAction>;
  elapsed: number;
}

const RunContext = createContext<RunContextValue | null>(null);

export function RunProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(runReducer, {
    run: initialRun,
    history: [],
    detectedCones: [],
  });

  // Load history from localStorage on client mount (useReducer initializer
  // runs during SSR where localStorage is unavailable, so history starts empty)
  useEffect(() => {
    const history = loadHistory();
    if (history.length > 0) {
      dispatch({ type: "IMPORT_HISTORY", results: history });
    }
  }, []);

  const elapsed =
    state.run.startTime && state.run.endTime
      ? state.run.endTime - state.run.startTime
      : state.run.startTime
        ? Date.now() - state.run.startTime
        : 0;

  return (
    <RunContext.Provider value={{ ...state, dispatch, elapsed }}>
      {children}
    </RunContext.Provider>
  );
}

export function useRun() {
  const context = useContext(RunContext);
  if (!context) {
    throw new Error("useRun must be used within a RunProvider");
  }
  return context;
}
